// src/pages/Categories/CategoryDetail.tsx

import { Card, Descriptions, Button, message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { getCategory } from '@/api/categories';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore'; // ✅ 导入
// import type { ProductCategory } from '@/types/category';

/**
 * 产品类别详情页面
 */
export const CategoryDetail = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const developerId = getCurrentDeveloperId(); // ✅ 获取开发者 ID

  // ✅ 查询添加开发者 ID 和 enabled 条件
  const categoryQuery = useQuery({
    queryKey: ['category', developerId, categoryId], // ✅ 添加开发者 ID
    queryFn: () => {
      if (!categoryId) {
        throw new Error('类别 ID 不能为空');
      }
      return getCategory(categoryId);
    },
    enabled: !!categoryId && !!developerId, // ✅ 只有登录后且有类别ID才请求
  });

  const category = categoryQuery.data;
  const loading = categoryQuery.isLoading;
  const error = categoryQuery.error;

  /**
   * 处理返回列表
   */
  const handleBack = (): void => {
    navigate('/categories');
  };

  /**
   * 处理编辑（跳转到编辑页面，如果后端支持）
   */
  const handleEdit = (): void => {
    message.info('编辑功能暂未实现');
    // 实际项目中可跳转到编辑页面
    // navigate(`/categories/${categoryId}/edit`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载类别详情"
        className="mb-4"
      />
    );
  }

  if (!category) {
    return (
      <ErrorAlert 
        message="未找到类别" 
        description="指定的类别不存在"
        className="mb-4"
      />
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold">类别详情</h1>
        <div className="space-x-2">
          <Button onClick={handleBack}>返回列表</Button>
          <Button type="primary" onClick={handleEdit}>
            编辑类别
          </Button>
        </div>
      </div>

      <Card title="基本信息" className="mb-6">
        <Descriptions column={1} bordered>
          <Descriptions.Item label="类别名称">{category.name}</Descriptions.Item>
          <Descriptions.Item label="类别ID">{category.id}</Descriptions.Item>
          <Descriptions.Item label="层级">{category.level}</Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {new Date(category.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {new Date(category.updated_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {category.children && category.children.length > 0 && (
        <Card title="子类别" className="mb-6">
          <div className="space-y-2">
            {category.children.map((child) => (
              <div key={child.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span>{child.name}</span>
                <Button 
                  size="small" 
                  onClick={() => navigate(`/categories/${child.id}`)}
                >
                  查看详情
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};